import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import UserInfo from "../../components/UserInfo/UserInfo";
import UserStats from "../../components/UserStats/UserStats";
import DataPeriodInfo from "../../components/DataPeriodInfo/DataPeriodInfo";
import DeviceSelector from "../../components/DeviceSelector/DeviceSelector";
import ThemeToggle from "../../components/ThemeToggle/ThemeToggle";
/* Por el repositorio, no por `api` directamente: asi la demostracion se
   resuelve contra datos simulados en vez de pedirlos a AWS y recibir un 401. */
import telemetryRepository from "../../services/repositories/telemetryRepository";
import { useDevices } from "../../context/DeviceContext";
import { useUserConfig } from "../../context/UserConfigContext";
import { useUnit } from "../../context/UnitContext";
import {
  AVAILABLE_UNITS,
  calculateHousingBillByLiters,
} from "../../utils/unitConversion";
import authService from "../../services/auth";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import "./Profile.css";

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
);

export default function Profile() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [, setDataInfo] = useState(null);
  const [availablePeriod, setAvailablePeriod] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const reportRef = useRef(null);
  const navigate = useNavigate();

  const {
    householdMembers,
    /* setHouseholdMembers, */
    dailyGoal,
    setDailyGoal,
    selectedTariff,
    setSelectedTariff,
    AVAILABLE_TARIFFS,
  } = useUserConfig();

  const { selectedUnit, setSelectedUnit } = useUnit();
  const { selectedDevice } = useDevices();

  // Estados para datos reales del usuario
  const [userStats, setUserStats] = useState({
    monthlyConsumption: "0",
    savings: "0",
    monitoredDays: "0",
  });

  // Cargar información del usuario
  useEffect(() => {
    const getUserInfo = () => {
      const info = authService.getUserInfo();
      setUserInfo(info);
    };
    getUserInfo();
  }, []);

  // Calcular estadísticas reales del usuario
  useEffect(() => {
    const calculateUserStats = async () => {
      if (!selectedDevice?.device_id) {
        setUserStats({
          monthlyConsumption: "0",
          savings: "0",
          monitoredDays: "0",
        });
        return;
      }

      try {
        const response = await telemetryRepository.getAggregated(
          selectedDevice.device_id,
          30,
        );

        if (response && response.dailyData && response.dailyData.length > 0) {
          const realData = response.dailyData;

          // Calcular consumo total
          const totalConsumption = realData.reduce(
            (sum, day) => sum + (day.consumption || 0),
            0,
          );

          // Calcular consumo promedio diario
          const avgDailyConsumption = totalConsumption / realData.length;

          // Proyectar consumo mensual (30 días)
          const monthlyProjection = avgDailyConsumption * 30;

          // Calcular ahorro vs promedio recomendado (360L por persona por día)
          const recommendedDaily = householdMembers * 360;
          const savingsPercentage =
            recommendedDaily > 0
              ? Math.max(
                  0,
                  ((recommendedDaily - avgDailyConsumption) /
                    recommendedDaily) *
                    100,
                )
              : 0;

          setUserStats({
            monthlyConsumption: monthlyProjection
              .toFixed(0)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
            savings: savingsPercentage.toFixed(0),
            monitoredDays: realData.length.toString(),
          });
        } else {
          // Si no hay datos reales, mantener valores en 0
          setUserStats({
            monthlyConsumption: "0",
            savings: "0",
            monitoredDays: "0",
          });
        }
      } catch (error) {
        console.error("Error calculando estadísticas:", error);
        setUserStats({
          monthlyConsumption: "0",
          savings: "0",
          monitoredDays: "0",
        });
      }
    };

    calculateUserStats();
  }, [selectedDevice?.device_id, householdMembers]);

  // Cargar información del período disponible al montar el componente
  useEffect(() => {
    const loadAvailablePeriod = async () => {
      if (!selectedDevice?.device_id) {
        setAvailablePeriod({
          days: 0,
          startDate: format(new Date(), "yyyy-MM-dd"),
          endDate: format(new Date(), "yyyy-MM-dd"),
          isRealData: false,
        });
        setDataInfo({
          hasRealData: false,
          dataPoints: 0,
          daysWithData: 0,
        });
        return;
      }

      try {
        const response = await telemetryRepository.getAggregated(
          selectedDevice.device_id,
          30,
        );

        if (response && response.dailyData && response.dailyData.length > 0) {
          const realData = response.dailyData;
          setAvailablePeriod({
            days: realData.length,
            startDate: realData[realData.length - 1].date,
            endDate: realData[0].date,
            isRealData: true,
          });
          setDataInfo({
            hasRealData: true,
            dataPoints: response.total_records_processed || 0,
            daysWithData: realData.length,
          });
        } else {
          setAvailablePeriod({
            days: 0,
            startDate: format(new Date(), "yyyy-MM-dd"),
            endDate: format(new Date(), "yyyy-MM-dd"),
            isRealData: false,
          });
          setDataInfo({
            hasRealData: false,
            dataPoints: 0,
            daysWithData: 0,
          });
        }
      } catch (error) {
        console.error("Error cargando período disponible:", error);
      }
    };

    loadAvailablePeriod();
  }, [selectedDevice?.device_id]);

  // Obtener solo datos reales disponibles (máximo 30 días)
  const fetchRealAvailableData = async () => {
    if (!selectedDevice?.device_id) {
      throw new Error("No hay dispositivo seleccionado");
    }

    try {
      console.log("📊 Solicitando datos para reporte desde Profile.jsx...");
      const response = await telemetryRepository.getAggregated(
        selectedDevice.device_id,
        30,
      );

      console.log("📈 Respuesta del servidor:", response);

      if (response && response.dailyData && response.dailyData.length > 0) {
        const realData = response.dailyData;
        const daysWithData = realData.length;

        // Transformar al formato esperado por el PDF (igual que ReportGenerator)
        const processedData = realData.map((day) => {
          const consumption = parseFloat(day.consumption) || 0;
          const hoursWithFlow = day.hourlyBreakdown
            ? day.hourlyBreakdown.filter((h) => (h.consumption || 0) > 0).length
            : 0;

          return {
            date: day.date,
            consumption: consumption,
            valveOpenHours: hoursWithFlow, // Horas con flujo activo (dato real)
            avgFlowRate: parseFloat(day.avgFlowRate) || 0,
            maxFlowRate: parseFloat(day.maxFlowRate) || 0,
            isRealData: true,
          };
        });

        // Ordenar de más antiguo a más reciente
        const sortedData = processedData.sort(
          (a, b) => new Date(a.date) - new Date(b.date),
        );

        setDataInfo({
          hasRealData: true,
          dataPoints: response.total_records_processed || 0,
          daysWithData: daysWithData,
        });

        setAvailablePeriod({
          days: daysWithData,
          startDate: sortedData[0].date,
          endDate: sortedData[sortedData.length - 1].date,
          isRealData: true,
        });

        console.log(`✅ Usando ${daysWithData} días de datos 100% reales`);
        console.log("📊 Primer día procesado:", sortedData[0]);
        return sortedData;
      } else {
        throw new Error(
          "No hay datos disponibles para el período seleccionado",
        );
      }
    } catch (error) {
      console.error("❌ Error obteniendo datos:", error);
      throw error;
    }
  };

  // Generar reporte
  const generateReport = async () => {
    setLoading(true);

    try {
      const data = await fetchRealAvailableData();
      setReportData(data);

      // Esperar a que los gráficos se rendericen
      setTimeout(() => {
        generatePDF(data);
      }, 1000);
    } catch (error) {
      console.error("Error generando reporte:", error);
      alert("Error al generar el reporte. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // Generar PDF
  const generatePDF = async (data) => {
    // Validar que tenemos datos
    if (!data || data.length === 0) {
      alert("No hay datos disponibles para generar el reporte");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Función auxiliar para validar números
    const safeNumber = (value, defaultValue = 0) => {
      if (
        value === undefined ||
        value === null ||
        isNaN(value) ||
        !isFinite(value)
      ) {
        return defaultValue;
      }
      return value;
    };

    // Título principal
    pdf.setFontSize(20);
    pdf.setFont(undefined, "bold");
    pdf.text("Reporte de Consumo de Agua", pageWidth / 2, 20, {
      align: "center",
    });

    // Subtítulo con fecha
    pdf.setFontSize(12);
    pdf.setFont(undefined, "normal");
    let dateRangeText = "";
    if (availablePeriod) {
      const startDate = format(
        new Date(availablePeriod.startDate),
        "dd/MM/yyyy",
        { locale: es },
      );
      const endDate = format(new Date(availablePeriod.endDate), "dd/MM/yyyy", {
        locale: es,
      });
      dateRangeText = `${startDate} - ${endDate} (${availablePeriod.days} días)`;
    } else {
      dateRangeText = `Últimos ${data.length} días`;
    }
    pdf.text(`Período: ${dateRangeText}`, pageWidth / 2, 30, {
      align: "center",
    });

    // Estadísticas generales con validaciones
    const totalConsumption = safeNumber(
      data.reduce((sum, day) => sum + safeNumber(day.consumption, 0), 0),
    );
    const avgDailyConsumption =
      data.length > 0 ? safeNumber(totalConsumption / data.length) : 0;

    const consumptions = data.map((d) => safeNumber(d.consumption, 0));
    const maxConsumption =
      consumptions.length > 0 ? Math.max(...consumptions) : 0;

    // Cambio: ahora sumamos las horas con flujo (dato real) en lugar de operaciones estimadas
    const totalValveChanges = data.reduce(
      (sum, day) => sum + safeNumber(day.valveOpenHours, 0),
      0,
    );

    const flowRates = data.map((d) => safeNumber(d.avgFlowRate, 0));
    const avgFlowRate =
      flowRates.length > 0
        ? safeNumber(
            flowRates.reduce((sum, rate) => sum + rate, 0) / flowRates.length,
          )
        : 0;

    // Cálculos de facturación
    const billInfo = calculateHousingBillByLiters(
      totalConsumption,
      "governmental",
    );
    const avgDailyCost =
      data.length > 0 ? safeNumber(billInfo.total / data.length) : 0;
    const monthlyProjection = safeNumber(avgDailyCost * 30);

    let yPos = 45;

    // Resumen ejecutivo
    pdf.setFontSize(14);
    pdf.setFont(undefined, "bold");
    pdf.text("Resumen Ejecutivo", 20, yPos);
    yPos += 10;

    pdf.setFontSize(10);
    pdf.setFont(undefined, "normal");
    pdf.text(
      `• Consumo total: ${safeNumber(totalConsumption).toFixed(2)} litros`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Consumo promedio diario: ${safeNumber(avgDailyConsumption).toFixed(2)} litros`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Consumo máximo en un día: ${safeNumber(maxConsumption).toFixed(2)} litros`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Total de horas con flujo activo: ${safeNumber(totalValveChanges, 0)} horas`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Flujo promedio: ${safeNumber(avgFlowRate).toFixed(2)} L/min`,
      25,
      yPos,
    );
    yPos += 15;

    // Información de facturación
    pdf.setFontSize(14);
    pdf.setFont(undefined, "bold");
    pdf.text("Información de Facturación", 20, yPos);
    yPos += 10;

    pdf.setFontSize(10);
    pdf.setFont(undefined, "normal");
    pdf.text(
      `• Costo total del período: $${safeNumber(billInfo.total).toFixed(2)} MXN`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Cargo fijo: $${safeNumber(billInfo.fixedCharge).toFixed(2)} MXN`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Costo variable: $${safeNumber(billInfo.variableCost).toFixed(2)} MXN`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Consumo facturado: ${safeNumber(billInfo.cubicMeters).toFixed(2)} m³`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Costo promedio diario: $${safeNumber(avgDailyCost).toFixed(2)} MXN`,
      25,
      yPos,
    );
    yPos += 6;
    pdf.text(
      `• Proyección mensual: $${safeNumber(monthlyProjection).toFixed(2)} MXN`,
      25,
      yPos,
    );
    yPos += 15;

    // Capturar gráficos
    if (reportRef.current) {
      try {
        const canvas = await html2canvas(reportRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          // El PDF se imprime sobre papel blanco. Si el usuario está en tema
          // oscuro, la captura saldría con gráficas oscuras sobre fondo blanco.
          // Se fuerza el tema claro sólo en el clon que html2canvas dibuja: la
          // pantalla del usuario no cambia.
          onclone: (doc) => doc.documentElement.setAttribute("data-theme", "light"),
        });

        const imgData = canvas.toDataURL("image/png");
        const imgWidth = pageWidth - 40;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // Verificar si necesitamos una nueva página
        if (yPos + imgHeight > pageHeight - 20) {
          pdf.addPage();
          yPos = 20;
        }

        pdf.addImage(imgData, "PNG", 20, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 10;
      } catch (error) {
        console.error("Error capturando gráficos:", error);
      }
    }

    // Tabla de datos diarios con costos (últimos 10 días)
    if (yPos > pageHeight - 80) {
      pdf.addPage();
      yPos = 20;
    }

    pdf.setFontSize(14);
    pdf.setFont(undefined, "bold");
    pdf.text("Últimos 10 días - Detalle y Costos", 20, yPos);
    yPos += 15;

    // Encabezados de tabla
    pdf.setFontSize(8);
    pdf.setFont(undefined, "bold");
    pdf.text("Fecha", 25, yPos);
    pdf.text("Consumo (L)", 55, yPos);
    pdf.text("Costo Diario", 85, yPos);
    pdf.text("Horas Activas", 120, yPos);
    pdf.text("Flujo Prom.", 155, yPos);
    pdf.text("Flujo Máx.", 185, yPos);
    yPos += 8;

    // Datos de tabla (últimos 10 días)
    pdf.setFont(undefined, "normal");
    const last10Days = data.slice(-10);
    last10Days.forEach((day) => {
      const formattedDate = format(new Date(day.date), "dd/MM", { locale: es });
      const dailyBill = calculateHousingBillByLiters(
        safeNumber(day.consumption, 0),
        "governmental",
      );
      const dailyCost = safeNumber(dailyBill.total / 30).toFixed(2); // Prorrateado mensualmente

      pdf.text(formattedDate, 25, yPos);
      pdf.text(safeNumber(day.consumption).toFixed(1), 55, yPos);
      pdf.text(`$${dailyCost}`, 85, yPos);
      pdf.text(safeNumber(day.valveOpenHours).toFixed(1), 120, yPos);
      pdf.text(`${safeNumber(day.avgFlowRate).toFixed(1)}`, 155, yPos);
      pdf.text(`${safeNumber(day.maxFlowRate).toFixed(1)}`, 185, yPos);
      yPos += 6;
    });

    // Footer
    pdf.setFontSize(8);
    pdf.setFont(undefined, "italic");
    pdf.text(
      `Reporte generado el ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: es })}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" },
    );
    pdf.text(
      "Sistema Nissa Guard - Control Inteligente de Agua",
      pageWidth / 2,
      pageHeight - 5,
      { align: "center" },
    );

    // Descargar PDF
    const fileName = `reporte-consumo-agua-${format(new Date(), "yyyy-MM-dd")}.pdf`;
    pdf.save(fileName);
  };

  /* const handleHouseholdChange = (e) => {
    const value = e.target.value;
    if (value === "" || parseInt(value) > 0) {
      setHouseholdMembers(value === "" ? "" : parseInt(value));
    }
  }; */

  const handleDailyGoalChange = (e) => {
    const value = e.target.value;
    if (value === "" || parseInt(value) > 0) {
      setDailyGoal(value === "" ? "" : parseInt(value));
    }
  };

  const handleUnitChange = (e) => {
    setSelectedUnit(e.target.value);
  };

  const handleTariffChange = (e) => {
    setSelectedTariff(e.target.value);
  };

  const handleLogout = () => {
    authService.logout();
    navigate("/login");
  };

  return (
    <div className="profile-container">
      <h1>Perfil de Usuario</h1>

      {/* Selector de dispositivos */}
      <DeviceSelector />

      {userInfo && (
        <UserInfo
          user={{
            name: userInfo.name || userInfo.email?.split("@")[0] || "Usuario",
            email: userInfo.email || "No disponible",
          }}
        />
      )}
      <UserStats stats={userStats} />
      <DataPeriodInfo />

      <div className="settings-section">
        <h2>Configuración</h2>
        <div className="household-section">
          {/* Apariencia: el mismo selector de tema que la barra superior, aquí
              con etiqueta, que es donde alguien lo busca al configurar. */}
          <div className="household-input household-input--inline">
            <label htmlFor="themePreference">Apariencia</label>
            <ThemeToggle variant="labelled" />
          </div>
          <div className="household-input">
            <label htmlFor="householdMembers">
              Identificador del dispositivo:
            </label>
            {/* El campo nunca fue editable: no tenía `onChange`, así que React
                avisaba en consola y el usuario veía una caja de texto que no
                acepta texto. Se declara de sólo lectura, que es lo que es. */}
            <input type="text" id="deviceId" value="Edificio 9" readOnly />
          </div>
          <div className="household-input">
            <label htmlFor="dailyGoal">Meta diaria de litros (opcional):</label>
            <input
              type="number"
              id="dailyGoal"
              min="1"
              value={dailyGoal}
              onChange={handleDailyGoalChange}
              placeholder={`Ej: ${householdMembers * 330}`}
            />
          </div>
          <div className="household-input">
            <label htmlFor="unitSelection">Unidad de medida:</label>
            <select
              id="unitSelection"
              value={selectedUnit}
              onChange={handleUnitChange}
              className="unit-select"
            >
              {AVAILABLE_UNITS.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
          <div className="household-input">
            <label htmlFor="tariffSelection">Tipo de Tarifa SADM:</label>
            <select
              id="tariffSelection"
              value={selectedTariff}
              onChange={handleTariffChange}
              className="unit-select"
            >
              {AVAILABLE_TARIFFS.map((tariff) => (
                <option key={tariff.value} value={tariff.value}>
                  {tariff.label}
                </option>
              ))}
            </select>
            <small
              className="form-hint"
            >
              {AVAILABLE_TARIFFS.find((t) => t.value === selectedTariff)
                ?.description || ""}
            </small>
          </div>
          {/*  <p className="household-info">
            Consumo diario estimado: {householdMembers ? householdMembers * 360 : 0} L/día<br/>
            <span style={{color: '#888'}}>El promedio es <b>360 L</b> por persona al día.</span>
          </p> */}
        </div>
      </div>

      {/* Sección de reportes */}
      <div className="reports-section">
        <h2>Reportes</h2>
        <div className="report-download-section">
          <p>Genera reportes detallados del consumo de agua en formato PDF</p>

          <div className="report-controls">
            <button
              className="generate-report-btn"
              onClick={generateReport}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Generando...
                </>
              ) : (
                <>
                  <span className="pdf-icon"></span>
                  Generar Reporte PDF
                </>
              )}
            </button>
          </div>

          {/* Información del período disponible */}
          {availablePeriod && (
            <div className="period-info">
              <div className="period-details">
                {/*<div className={`period-badge ${availablePeriod.isRealData ? 'real' : 'simulated'}`}>
                  {availablePeriod.isRealData ? '✓ Datos Reales' : '⚠ Datos Simulados'}
                </div>*/}
                {/* <div className="period-text">
                  Período disponible: {availablePeriod.days} días
                </div> */}
                {/*  <div className="period-dates">
                  {format(new Date(availablePeriod.startDate), 'dd/MM/yyyy', { locale: es })} - {format(new Date(availablePeriod.endDate), 'dd/MM/yyyy', { locale: es })}
                </div>
                {dataInfo && dataInfo.hasRealData && (
                  <div className="data-points">
                    {dataInfo.dataPoints} puntos de datos reales
                  </div>
                )}
                {!availablePeriod.isRealData && (
                  <div className="period-note">
                    Datos simulados para demostración
                  </div>
                )} */}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Vista previa del reporte (oculta, solo para captura) */}
      {reportData && (
        <div
          ref={reportRef}
          className="report-preview"
          style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
        >
          <div className="charts-container">
            {/* Charts removed as requested */}
          </div>
        </div>
      )}

      {/* Sección de información del usuario y logout */}
      <div className="user-session-section">
        <h2>Información de Sesión</h2>

        {userInfo && (
          <div className="current-user-info">
            <div className="user-details">
              <PersonIcon className="user-avatar-icon" />
              <div className="user-text-info">
                <h3>
                  {userInfo.name || userInfo.email?.split("@")[0] || "Usuario"}
                </h3>
                <p className="user-email">{userInfo.email}</p>
                <p className="user-role">
                  Rol:{" "}
                  {userInfo["cognito:groups"]?.includes("Admins")
                    ? "Administrador"
                    : userInfo["cognito:groups"]?.includes("Operators")
                      ? "Operador"
                      : "Visualizador"}
                </p>
              </div>
            </div>

            <div className="session-actions">
              <button className="logout-button" onClick={handleLogout}>
                <LogoutIcon />
                Cerrar Sesión
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
